import { useEffect, useRef, useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Download, Eraser, Loader2, Mail, PenLine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import {
  descargarNotaFirmada,
  enviarCodigoFirma,
  firmarNotaEncargo,
  getNotaParaFirmar,
  registrarVistaNota,
} from "@/lib/notas-encargo.functions";

/** Página donde el propietario lee y firma la nota de encargo. */
export const Route = createFileRoute("/firmar/$token")({
  loader: async ({ params }) => {
    if (!/^[A-Za-z0-9_-]{16,40}$/.test(params.token)) throw notFound();
    const nota = await getNotaParaFirmar({ data: { token: params.token } });
    if (!nota) throw notFound();
    return nota;
  },
  head: ({ params }) =>
    buildSeo({
      title: "Firma de la nota de encargo | Bizaclia",
      description: "Firma electrónica de la nota de encargo.",
      path: `/firmar/${params.token}`,
      noindex: true,
    }),
  component: FirmarPage,
  notFoundComponent: () => (
    <main className="flex min-h-[60vh] items-center justify-center px-4 py-16">
      <div className="max-w-lg text-center">
        <h1 className="font-display text-3xl font-bold tracking-tight">Enlace no válido</h1>
        <p className="mt-4 text-muted-foreground">Revisa el enlace o pide a la agencia que te lo vuelva a enviar.</p>
      </div>
    </main>
  ),
});

/** Lienzo para dibujar la firma con el dedo o el ratón. */
function PadFirma({ onChange }: { onChange: (png: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dibujando = useRef(false);
  const vacio = useRef(true);

  useEffect(() => {
    const c = ref.current!;
    const ajustar = () => {
      const r = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = r.width * dpr;
      c.height = r.height * dpr;
      const ctx = c.getContext("2d")!;
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0B1F1A";
      vacio.current = true;
      onChange(null);
    };
    ajustar();
    window.addEventListener("resize", ajustar);
    return () => window.removeEventListener("resize", ajustar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const punto = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    ref.current!.setPointerCapture(e.pointerId);
    dibujando.current = true;
    const ctx = ref.current!.getContext("2d")!;
    const p = punto(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.PointerEvent) => {
    if (!dibujando.current) return;
    const ctx = ref.current!.getContext("2d")!;
    const p = punto(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    vacio.current = false;
  };
  const end = () => {
    if (!dibujando.current) return;
    dibujando.current = false;
    if (!vacio.current) onChange(ref.current!.toDataURL("image/png"));
  };
  const borrar = () => {
    const c = ref.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    vacio.current = true;
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl border-2 border-dashed bg-white">
        <canvas
          ref={ref}
          className="h-44 w-full touch-none rounded-xl"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        <span className="pointer-events-none absolute bottom-3 left-4 right-4 border-t border-slate-300 pt-1 text-xs text-slate-400">
          Firma aquí con el dedo o el ratón
        </span>
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={borrar}>
        <Eraser /> Borrar
      </Button>
    </div>
  );
}

function Paso({ n, titulo, activo, hecho }: { n: number; titulo: string; activo: boolean; hecho: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "flex size-7 items-center justify-center rounded-full text-sm font-semibold",
          hecho ? "bg-emerald-500 text-white" : activo ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {hecho ? <CheckCircle2 className="h-4 w-4" /> : n}
      </span>
      <span className={cn("text-sm", activo || hecho ? "font-medium" : "text-muted-foreground")}>{titulo}</span>
    </div>
  );
}

function FirmarPage() {
  const nota = Route.useLoaderData();
  const { token } = Route.useParams();
  const [leido, setLeido] = useState(false);
  const [codigoEnviado, setCodigoEnviado] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [firma, setFirma] = useState<string | null>(null);
  const [acepto, setAcepto] = useState(false);
  const [firmada, setFirmada] = useState(nota.estado === "firmada");

  useEffect(() => {
    void registrarVistaNota({ data: { token } }).catch(() => {});
  }, [token]);

  const mCodigo = useMutation({
    mutationFn: () => enviarCodigoFirma({ data: { token } }),
    onSuccess: (r) => {
      setCodigoEnviado(r.email);
      toast.success(`Código enviado a ${r.email}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const mFirmar = useMutation({
    mutationFn: () => firmarNotaEncargo({ data: { token, codigo, firma_png: firma!, acepto: true } }),
    onSuccess: () => {
      setFirmada(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const mPdf = useMutation({
    mutationFn: () => descargarNotaFirmada({ data: { token } }),
    onSuccess: (r) => window.open(r.url, "_blank", "noopener"),
    onError: (e: Error) => toast.error(e.message),
  });

  const parrafos = nota.contenido.split(/\n\n+/);
  const bloqueada = nota.estado === "cancelada" || nota.caducada;

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="flex items-center gap-3">
          {nota.agencia.logo_url ? (
            <img src={nota.agencia.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain" />
          ) : null}
          <div>
            <p className="text-sm text-muted-foreground">{nota.agencia.nombre}</p>
            <h1 className="font-display text-2xl font-bold tracking-tight">Nota de encargo</h1>
          </div>
        </header>

        {firmada ? (
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center dark:border-emerald-900 dark:bg-emerald-950/30">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h2 className="mt-3 text-xl font-semibold">Firmada</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Gracias, {nota.firmante.split(/\s+/)[0]}. Te hemos enviado una copia por email y puedes descargarla cuando
              quieras desde este enlace.
            </p>
            <Button className="mt-4" onClick={() => mPdf.mutate()} disabled={mPdf.isPending}>
              <Download /> Descargar mi copia (PDF)
            </Button>
          </section>
        ) : bloqueada ? (
          <section className="rounded-2xl border border-dashed bg-background p-6 text-center text-muted-foreground">
            {nota.estado === "cancelada"
              ? "La agencia ha anulado esta nota. Si tienes dudas, escríbele directamente."
              : "Este enlace ha caducado. Pide a la agencia que te envíe uno nuevo."}
          </section>
        ) : (
          <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-2xl border bg-background p-4">
            <Paso n={1} titulo="Lee la nota" activo={!leido} hecho={leido} />
            <Paso n={2} titulo="Confirma tu email" activo={leido && codigo.length < 6} hecho={codigo.length === 6} />
            <Paso n={3} titulo="Firma" activo={codigo.length === 6} hecho={false} />
          </div>
        )}

        <article className="space-y-4 rounded-2xl border bg-background p-6 text-[15px] leading-relaxed shadow-sm sm:p-8">
          {parrafos.map((p, i) =>
            i === 0 ? null : p === p.toUpperCase() && p.length < 60 ? (
              <h3 key={i} className="pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {p}
              </h3>
            ) : (
              <p key={i}>{p}</p>
            ),
          )}
          <p className="flex items-start gap-2 border-t pt-4 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="break-all">Huella del documento (SHA-256): {nota.hash}</span>
          </p>
        </article>

        {!firmada && !bloqueada && (
          <section className="space-y-6 rounded-2xl border bg-background p-6 shadow-sm sm:p-8">
            {!leido ? (
              <Button size="lg" className="w-full" onClick={() => setLeido(true)}>
                He leído la nota, quiero firmarla
              </Button>
            ) : (
              <>
                <div className="space-y-3">
                  <h2 className="flex items-center gap-2 font-semibold">
                    <Mail className="h-5 w-5 text-primary" /> Confirma que eres tú
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Te enviamos un código de 6 cifras a {codigoEnviado ?? nota.emailOculto}.
                  </p>
                  {codigoEnviado ? (
                    <div className="space-y-2">
                      <InputOTP maxLength={6} value={codigo} onChange={setCodigo} inputMode="numeric">
                        <InputOTPGroup>
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <InputOTPSlot key={i} index={i} />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => mCodigo.mutate()}
                        disabled={mCodigo.isPending}
                      >
                        No me llega, enviar otro
                      </button>
                    </div>
                  ) : (
                    <Button onClick={() => mCodigo.mutate()} disabled={mCodigo.isPending}>
                      {mCodigo.isPending ? <Loader2 className="animate-spin" /> : <Mail />} Enviarme el código
                    </Button>
                  )}
                </div>

                <div className={cn("space-y-3", codigo.length < 6 && "pointer-events-none opacity-40")}>
                  <h2 className="flex items-center gap-2 font-semibold">
                    <PenLine className="h-5 w-5 text-primary" /> Tu firma
                  </h2>
                  <PadFirma onChange={setFirma} />
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked={acepto} onCheckedChange={(v) => setAcepto(v === true)} className="mt-0.5" />
                    <span>
                      Yo, {nota.firmante}, he leído y acepto el contenido íntegro de esta nota de encargo, y la firmo de
                      forma electrónica.
                    </span>
                  </label>
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={codigo.length < 6 || !firma || !acepto || mFirmar.isPending}
                    onClick={() => mFirmar.mutate()}
                  >
                    {mFirmar.isPending ? <Loader2 className="animate-spin" /> : <PenLine />} Firmar
                  </Button>
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
